<template>
  <div class="relative">
    <ConnectionInform />
    <div
      v-if="embedSrc"
      class="embed-container"
      :class="{
        'no-caption': !hasActiveEmbedSlideTitle,
      }"
    >
      <div v-if="isLoading" class="spinner-container">
        <aha-spinner size="xl" :variant="isTextColourLight ? 'white' : 'dark'" />
      </div>
      <div
        v-if="isIframeCode"
        class="iframe-container"
        ref="iframe-container"
        v-html="sanitizedEmbedLink"
      >
      </div>
      <iframe
        v-else-if="!embedSrcError"
        :src="embedSrc"
        frameborder="0"
        class="embed-iframe"
        ref="embed-iframe"
        allowfullscreen
        mozallowfullscreen="true"
        webkitallowfullscreen="true"
        @error="handleIframeError"
        @load="handleIframeLoad"
      ></iframe>
      <div v-else class="embed-error">
      </div>
    </div>
    <div v-else class="embed-placeholder">
    </div>
    <div
      class="p-0 caption"
      v-if="hasActiveEmbedSlideTitle"
      :class="{
        'small-text': activeEmbedSlide.title && activeEmbedSlide.title.length > 64
      }"
    >
      <SlideTitleEditor
        class="p-0 youtube-caption-text"
        :class="{'has-background': hasActiveEmbedSlideTitle}"
        type="slide-title"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ConnectionInform from '@/components-v2/presentation-editor/middle/connection/ConnectionInform.vue'
import SlideTitleEditor from '@/components-v2/presentation-editor/middle/SlideTitleEditor.vue'
import { getEmbeddableLink } from '@/business-logic/embed-slide'

export default {
  name: 'EmbedSlideCastingView',
  components: {
    ConnectionInform,
    SlideTitleEditor,
  },
  props: {
    slideActive: {
      type: Object,
      required: true,
    },
    presentation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
    }
  },
  computed: {
    ...mapGetters(['isPresenting', 'isTextColourLight']),
    ...mapGetters('embedSlide', ['activeEmbedSlide', 'embedSrcError']),
    isIframeCode() {
      return this.activeEmbedSlide?.attributes?.embedSrc?.includes('<iframe')
    },
    embedSrc() {
      const originalEmbedSrc = this.activeEmbedSlide?.attributes?.embedSrc
      if (!originalEmbedSrc) return null
      return getEmbeddableLink(originalEmbedSrc)
    },
    hasActiveEmbedSlideTitle() {
      return this.activeEmbedSlide.title && this.activeEmbedSlide.title.trim().length
    },
    sanitizedEmbedLink() {
      const embedIframeCode = this.embedSrc

      const template = document.createElement('template')
      template.innerHTML = embedIframeCode.trim() // Trim to avoid accidental whitespace
      const element = template.content.firstChild
      element.style.width = '100%'
      element.style.height = '100%'
      element.style.border = 'none'
      element.style.background = 'transparent'

      return element.outerHTML
    },

  },
  watch: {
    'slideActive.id': function (newSlideId) {
      if (newSlideId) {
        this.hasError = false
        this.isLoading = true
        this.loadEmbedSlideAction({
          slideId: newSlideId,
        })
      }
    },
    embedSrc(newVal) {
      if (newVal) {
        this.isLoading = true
      }
    },
  },

  mounted() {
    this.loadEmbedSlideAction({
      slideId: this.slideActive.id,
    })
    this.clearLoadingTimeout = setTimeout(() => {
      this.isLoading = false
    }, 25e3)
    this.$nextTick(() => {
      this.attachIframeListeners()
    })
  },
  updated() {
    this.$nextTick(() => {
      this.attachIframeListeners()
    })
    if (this.clearLoadingTimeout) {
      clearTimeout(this.clearLoadingTimeout)
    }
  },
  beforeDestroy() {
    if (this.clearLoadingTimeout) {
      clearTimeout(this.clearLoadingTimeout)
    }
  },
  methods: {
    ...mapActions('embedSlide', ['loadEmbedSlideAction', 'setEmbedSrcErrorAction']),
    handleIframeError() {
      this.hasError = true
      this.isLoading = false
    },
    handleIframeLoad() {
      this.isLoading = false
      if (this.clearLoadingTimeout) {
        clearTimeout(this.clearLoadingTimeout)
      }
    },
    attachIframeListeners() {
      const container = this.$refs['iframe-container']
      if (!container) return

      const iframe = container.querySelector('iframe')
      if (iframe && !iframe.dataset.listenerAttached) {
        iframe.dataset.listenerAttached = 'true' // Prevent multiple bindings

        iframe.addEventListener('load', () => {
          this.handleIframeLoad()
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.embed-container {
  position: relative;
  width: 910px;
  height: 512px;
  margin: 16px auto 16px auto;
  &.no-caption {
    width: 1031px;
    height: 580px;
    margin-bottom: 0;
  }
}

.embed-iframe {
  width: 100%;
  height: 100%;
  border: none;
  background-color: transparent;
}

.iframe-container {
  width: 100%;
  height: 100%;
  iframe {
    width: 100%;
    height: 100%;
    border: none;
    background-color: transparent;
  }
}

.caption {
  font-size: 25px;
  font-weight: bold;
}

.embed-placeholder,
.embed-error {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  color: #666;
  text-align: center;
  padding: 2rem;
}

.spinner-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  z-index: 1;
}

</style>
